import { Compiler } from 'inkjs/compiler/Compiler';
import { Story } from 'inkjs';
import { Dialog } from './dialog';
import ReactFocusLock from 'react-focus-lock';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { GameContext, GameEvents } from '@game-libs/models';
import { createRoot } from 'react-dom/client';
import { EventsManager } from '@game-libs/events-manager';

export const QuickGameDialog = (props?: {
  pathToInkFiles?: string;
  pathToSpeakerFiles?: string;
  containerId?: string;
}) => {
  let instance: GameDialog | null = (window as any).quick_game_dialog || null;

  if (instance) {
    return instance;
  }

  const { pathToInkFiles = '', pathToSpeakerFiles = '', containerId = '' } = props || {};

  let globalContainer: HTMLElement = document.getElementById(containerId || 'canvas-wrapper') || document.body;
  let story: Story;

  let closeCurrentDialog: (() => void) | null;

  const { actions } = GlobalGameStore();

  const openDialog = async (
    id: string,
    subPath?: string,
    isDismissable = true,
    /** Runs when the story ends or the player dismisses the dialog (before close). */
    onStoryClosed?: (storyId: string) => void,
  ): Promise<{
    story: Story;
    closeDialog: () => void;
  }> => {
    // If a dialog is already open, close it.
    closeCurrentDialog?.();

    const { emit } = EventsManager();

    emit(GameEvents.START_DIALOG, { storyId: id });

    const container = document.createElement('div');
    const root = createRoot(container);
    globalContainer.appendChild(container);

    const story = await fetchStory(id, subPath);
    actions.setContext(GameContext.DIALOG);

    const closeDialog = () => {
      if (container.isConnected) {
        actions.setContext(GameContext.IN_GAME);
        root.render(null);

        globalContainer.removeChild(container);
      }

      closeCurrentDialog = null;
    };

    closeCurrentDialog = closeDialog;

    root.render(
      <>
        {story && (
          <ReactFocusLock>
            <style>
              {`
                .dialog-wrapper {
                  position: absolute;
                  left: 50%;
                  transform: translateX(-50%);
                  display: flex;
                  bottom: 0;
                  width: var(--dialog-width, 100%);
                  padding: var(--menu-padding, var(--space-xs, 0.5em) var(--space-sm, 0.75em));
                  border-radius: var(--border-radius, 0);
                }
                  
                .line-speaker {
                  background: var(--speaker-background, #cdcdcd);
                  background-size: contain;
                  background-position: center;
                  flex-basis: var(--speaker-width, 150px);
                  flex-shrink: 0;
                  background-repeat: no-repeat;
                }

                .dialog-text-wrapper {
                  gap: var(--space-md, 1em);
                  background: var(--dialog-background, var(--white, #efefef));
                  display:flex;
                  flex: 1;
                  min-height: var(--dialog-text-height, 150px);
                  padding: var(--menu-inner-padding, var(--space-xs, 0.5em) var(--space-sm, 0.75em));
                  font-size: var(--dialog-font-size, var(--text-xl, 1.5em));
                  font-family: var(--dialog-font-family, inherit);
                  color: var(--text-color, #111111)
                }
                  
                .dialog-text {
                  flex: 1;
                  min-height: var(--dialog-text-height, 200px);
                  font-size: var(--dialog-font-size, var(--text-xl, 1.5em));
                  color: var(--dialog-text-color, #111111)
                }

                .dialog-text > div {
                  white-space: unset !important;
                }

                .choice-prompts {
                  position: absolute;
                  bottom: var(--choice-prompts-position-bottom, var(--space-md, 0.75em));
                  right: var(--choice-prompts-position-right, var(--space-lg, 0.75em));
                  display: flex;
                  gap: var(--choices-gap, var(--space-xl, 1.25em));
                  padding: var(--choice-prompts-padding, var(--space-xs, 0.5em) var(--space-sm, 0.75em));
                }

                .choice-prompts .choice-prompt {
                  font-size: var(--dialog-font-size, var(--text-xl, 1.5em));
                  text-align: start;
                  color: var(--button-text-color, inherit);
                  // background: var(--button-background, #ff4136);
                  padding: var(--space-xxs, 0.25em) var(--space-xs, 0.5em);
                  // border: unset;
                }

                .choice-prompts .no-choice-prompt {
                  font-size: var(--dialog-font-size, var(--text-xl, 1.5em));
                  color: var(--button-no-choice-text-color, inherit);
                  background: none;
                  padding: var(--space-xxs, 0.25em) var(--space-xs, 0.5em);
                  // border: unset;
                  &:before {
                    content: 'V';
                  }

                .choice-prompts .no-choice-prompt:focus {
                  outline: var(--button-outline, none);
                }

                .choice-prompts .choice-prompt:focus {
                  background: var(--button-focus-background, #85144b);
                  outline: var(--button-outline, none);
                }
            `}
            </style>
            <Dialog
              story={story}
              storyId={id}
              speakerAssets={pathToSpeakerFiles || ''}
              isDismissable={isDismissable}
              onFinished={() => {
                closeDialog();
              }}
            ></Dialog>
          </ReactFocusLock>
        )}
      </>,
    );

    return { story, closeDialog };
  };

  const fetchStory = async (storyId: string, subPath: string | undefined) => {
    const ink = await fetch(pathToInkFiles + (subPath ? '/' + subPath : '') + '/' + storyId + '.ink');
    const inkText = await ink.text();

    const { actions, state } = GlobalGameStore();

    try {
      story = new Compiler(inkText).Compile();

      const savedVariables = state.globalVariables.value;

      const storyVariables = Object.keys(
        Object.fromEntries(
          // @ts-expect-error Property '_globalVariables' is private and only accessible within class 'VariablesState'.ts(2341)
          story.variablesState._globalVariables,
        ),
      );

      storyVariables.map((variableKey: string) => {
        if (savedVariables?.[variableKey] !== undefined) {
          story.variablesState[variableKey] = savedVariables?.[variableKey];
        }
      });

      story.ObserveVariables(
        storyVariables,
        storyVariables.map((_: string) => (key: string, newValue: any) => {
          actions.setGlobalVariables({
            [key]: newValue,
          });
        }),
      );
    } catch (error) {
      actions.setContext(GameContext.IN_GAME);
      console.error('Error while loading story:', storyId, (error as Error).message);
      console.warn('Common dialog errors: not finding story .ink file, using undeclared variables, ...');
    }

    return story;
  };

  instance = {
    openDialog,
  };

  (window as any).quick_game_dialog = instance;

  return instance;
};

export type GameDialog = {
  openDialog: (
    id: string,
    subPath?: string,
    isDismissable?: boolean,
    onStoryClosed?: (storyId: string) => void,
  ) => Promise<{
    story: Story;
    closeDialog: () => void;
  }>;
};
